import { Injectable } from "@angular/core";
import { combineLatest, from, of } from "rxjs";
import { catchError, switchMap, tap } from "rxjs/operators";
import { HttpService } from "src/app/shared/http.service";
import { ImageCompressService } from "src/app/shared/image-compress.service";
import { SurveyPhotoTypeEnum } from "src/app/shared/Models/enums/SurveyPhotoTypeEnum";
import { PhotoIndexInfo } from "src/app/shared/Models/interfaces/PhotoIndexInfo.interface";
import { InsidePhotoDto } from "src/app/shared/Models/write/InsidePhotoDto";
import { ChangeInsideSurveyPhotoIndexDTO } from "src/app/shared/Models/write/ChangeInsideSurveyPhotoIndexDto";
import { ChangeOutsideSurveyPhotoIndexDTO } from "src/app/shared/Models/write/ChangeOutsideSurveyPhotoIndexDto";
import { OutsidePhotoDto } from "src/app/shared/Models/write/OutsidePhotoDto";
import { DataConvertingService } from "src/app/shared/services/data-converting.service";
import { APIEndpoints } from "src/config/apiEndpoint.config";
import { PictureInfo } from "../components/photo-and-remark/photo-viewmodel";

@Injectable({
  providedIn: "root",
})
export class PhotoService {
  constructor(
    private _httpService: HttpService,
    private _dataService: DataConvertingService,
    private _imageCompressor: ImageCompressService
  ) {}

  revertPhoto(id: string, surveyType: SurveyType) {
    switch (surveyType) {
      case SurveyType.Inside:
        return this._httpService.post(
          APIEndpoints.removeAllPhotoPointsByInsideSurveyPhotoId,
          { id }
        );
      case SurveyType.Outside:
        return this._httpService.post(
          APIEndpoints.removeAllPhotoPointsByOutsideSurveyPhotoId,
          { id }
        );
    }
  }

  async readFile(result): Promise<Event> {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.readAsDataURL(result);
      reader.onload = (event: Event) => {
        resolve(event);
      };
      reader.onerror = (error) => {
        reject(error);
      };
    });
  }

  removePhoto(id: string, surveyType: SurveyType) {
    switch (surveyType) {
      case SurveyType.Inside:
        return this._httpService.post(APIEndpoints.removePhotoToInsideSurvey, {
          id,
        });
      case SurveyType.Outside:
        return this._httpService.post(APIEndpoints.removePhotoToOutsideSurvey, {
          id,
        });
    }
  }

  changePhotoIndex(photos: PictureInfo[], surveyType: SurveyType) {
    switch (surveyType) {
      case SurveyType.Inside:
        return this.changePhotoIndexAsync(
          new ChangeInsideSurveyPhotoIndexDTO({
            photosIndexInfo: photos.map(
              (s) => <PhotoIndexInfo>{ index: s.index, photoId: s.data.id }
            ),
          })
        );
      case SurveyType.Outside:
        return this.changePhotoIndexAsync(
          new ChangeOutsideSurveyPhotoIndexDTO({
            photosIndexInfo: photos.map(
              (s) => <PhotoIndexInfo>{ index: s.index, photoId: s.data.id }
            ),
          })
        );
    }
  }

  changePhotoIndexAsync(
    dto: ChangeOutsideSurveyPhotoIndexDTO | ChangeInsideSurveyPhotoIndexDTO
  ) {
    return this._httpService.post(
      dto instanceof ChangeInsideSurveyPhotoIndexDTO
        ? APIEndpoints.changeInsideSurveyPhotoIndex
        : APIEndpoints.changeOutsideSurveyPhotoIndex,
      dto
    );
  }

  savePhoto(data: OutsidePhotoDto | InsidePhotoDto, surveyType: SurveyType) {
    var obs = [];
    if (!data.photoData && data.blob)
      obs.push(
        from(this._imageCompressor.getBase64fromFile(data.blob)).pipe(
          tap((b64) => (data.photoData = b64))
        )
      );
    if (!data.originalPhotoData && data.originalBlob)
      obs.push(
        from(this._imageCompressor.getBase64fromFile(data.originalBlob)).pipe(
          tap((b64) => (data.originalPhotoData = b64))
        )
      );
    return (
      obs.length > 0
        ? combineLatest(obs).pipe(switchMap(() => of(data)))
        : of(data)
    ).pipe(
      switchMap((data) => {
        if (!data.b64Parsed) {
          //Removing the metadata and mimeType of the data, the storing it sepparatly. No idea why.
          const parsedPhotoData = this._dataService.parseEmbeddedUrlFromBase64(
            data.photoData
          );
          data.photoData = parsedPhotoData.data;
          data.mimeType = parsedPhotoData.mimetype;
          //If we had originalPhotoData with metadata(a mimetype), we remove it and store the rest. Again, no idea why.
          if (data.originalPhotoData) {
            const originalPhotoData =
              this._dataService.parseEmbeddedUrlFromBase64(
                data.originalPhotoData
              );
            data.originalPhotoData = originalPhotoData.data;
          }

          data.b64Parsed = true;
        }
        var tempOriginalBlob = data.originalBlob;
        var tempBlob = data.blob;
        data.originalBlob = null;
        data.blob = null;
        var url;
        switch (surveyType) {
          case SurveyType.Inside:
            url =
              data.type == SurveyPhotoTypeEnum.OutsideCopperIntro ||
              data.type == SurveyPhotoTypeEnum.InsideCopperIntro ||
              data.type == SurveyPhotoTypeEnum.InsideFibreIntro ||
              data.type == SurveyPhotoTypeEnum.OutsideFibreIntro
                ? APIEndpoints.addIntroPhoto
                : APIEndpoints.addPhotoToInsideSurvey;
            break;
          case SurveyType.Outside:
            url = APIEndpoints.addPhotoToOutsideSurvey;
            break;
        }
        return this._httpService.post(url, data, false).pipe(
          catchError((e) => {
            data.originalBlob = tempOriginalBlob;
            data.blob = tempBlob;
            data.originalPhotoData = null;
            data.photoData = null;
            data.b64Parsed = false;
            throw e;
          })
        );
      })
    );
  }
}

export enum SurveyType {
  Inside,
  Outside,
}
